import {useCallback, useMemo, useState} from "react";

import {Skeleton} from "@components/ui/skeleton";
import {type LedgerFilters, type PeriodOptions, useVNextXeroPnL} from "@hooks/use-vnext";

import {ProvenanceErrorBoundary} from "./ProvenanceErrorBoundary";
import {ProvenancePanel} from "./ProvenancePanel";
import {TrustStrip} from "./TrustStrip";
import {DataSourceFootnote} from "./xero-pnl/DataSourceFootnote";
import {XeroPnlCfoQuestions} from "./xero-pnl/XeroPnlCfoQuestions";
import {XeroPnlCogsBreakdown} from "./xero-pnl/XeroPnlCogsBreakdown";
import {XeroPnlHeader} from "./xero-pnl/XeroPnlHeader";
import {XeroPnlKpiCards} from "./xero-pnl/XeroPnlKpiCards";
import {XeroPnlMonthlyPerformance} from "./xero-pnl/XeroPnlMonthlyPerformance";
import {XeroPnlOpexHighlights} from "./xero-pnl/XeroPnlOpexHighlights";
import {type XeroPnlCellInfo, XeroPnlTable} from "./xero-pnl/XeroPnlTable";
import {XeroPnlTrendCharts} from "./xero-pnl/XeroPnlTrendCharts";

interface XeroPnLTabProps {
    workspaceId: string;
    scenarioId: string;
    period?: string;
    selectedMonth?: string;
    customFrom?: string;
    customTo?: string;
    compare?: string;
    fyStartMonth?: number;
    currentMonth?: string;
    actualMonths?: number;
    forecastMonths?: number;
}

export function XeroPnLTab({
    workspaceId,
    scenarioId,
    period,
    selectedMonth,
    customFrom,
    customTo,
    compare,
    fyStartMonth,
    currentMonth,
    actualMonths = 12,
    forecastMonths = 0,
}: XeroPnLTabProps) {
    const periodOpts: PeriodOptions | undefined = period
        ? {period, selectedMonth, customFrom, customTo, compare, fyStartMonth, currentMonth}
        : undefined;

    const {data, isLoading} = useVNextXeroPnL(
        workspaceId,
        scenarioId,
        forecastMonths,
        actualMonths,
        periodOpts,
    );

    const [selectedCell, setSelectedCell] = useState<XeroPnlCellInfo | null>(null);

    const handleCellClick = useCallback((info: XeroPnlCellInfo) => {
        setSelectedCell(info);
    }, []);

    const handleOpenTransactions = useCallback((filters: LedgerFilters) => {
        window.dispatchEvent(new CustomEvent("vnext-open-transactions", {detail: {filters}}));
    }, []);

    const checksSummary = useMemo(() => data?.checks_summary ?? null, [data]);
    const monthlyPerformance = data?.monthly_performance ?? [];
    const trendPerformance = data?.trend_monthly_performance ?? monthlyPerformance;
    const cogsBuild = data?.cogs_build ?? [];
    const opexHighlights = data?.opex_highlights ?? [];
    const cfoQuestions = data?.cfo_questions ?? [];

    const meta = data?.meta ?? {
        workspace_name: "Xero Workspace",
        scenario_name: "Base",
        period_label: "Current period",
        n_actual_months: 0,
        n_forecast_months: 0,
        generated_at: "",
    };
    const hasForecastMonths = meta.n_forecast_months > 0 || monthlyPerformance.some((m) => m.status === "forecast");

    if (isLoading) {
        return (
            <div className="space-y-6 p-4">
                <Skeleton className="h-16 w-full rounded-xl" />
                <div className="grid grid-cols-6 gap-4">
                    {Array.from({length: 6}).map((_, i) => (
                        <Skeleton key={i} className="h-24 rounded-lg" />
                    ))}
                </div>
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-40 w-full" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
                <p>No P&L data available. Sync from Xero to get started.</p>
            </div>
        );
    }

    return (
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto bg-[#F8FAFC] p-4 pb-8">
            <div className="mx-auto w-full max-w-[1400px] space-y-6" data-tour="dashboard-pnl-tab">
                <header className="rounded-2xl bg-gradient-to-br from-[#209DB3] to-[#1A8C9E] p-6 shadow-sm">
                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-[#CCFFF2]">
                        Pecunio | P&L Summary
                    </p>
                    <div className="mt-3">
                        <XeroPnlHeader meta={meta} inverted />
                    </div>
                    {hasForecastMonths && (
                        <p className="mt-3 text-xs text-white/85">
                            Forecast months are shaded and marked <span className="rounded bg-white/15 px-1 py-0.5 font-semibold text-white">F</span>.
                        </p>
                    )}
                </header>

                <div data-tour="dashboard-pnl-kpis">
                    <XeroPnlKpiCards cards={data.kpi_cards} />
                </div>
                <XeroPnlTrendCharts months={trendPerformance} />

                <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm" data-tour="dashboard-pnl-table">
                    <XeroPnlTable pnl={data.pnl_table} onCellClick={handleCellClick} />
                </section>

                <div className="grid gap-6 xl:grid-cols-2">
                    <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                        <XeroPnlCogsBreakdown cogs={cogsBuild} showFootnote={false} />
                    </section>
                    <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                        <XeroPnlOpexHighlights opex={opexHighlights} showFootnote={false} />
                    </section>
                </div>

                {cfoQuestions.length > 0 && (
                    <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                        <XeroPnlCfoQuestions questions={cfoQuestions} showFootnote={false} />
                    </section>
                )}

                <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                    <XeroPnlMonthlyPerformance months={monthlyPerformance} showFootnote={false} />
                </section>

                <TrustStrip checksSummary={checksSummary} tab="pnl" />
                <DataSourceFootnote generatedAt={meta.generated_at} />
            </div>

            {selectedCell && (
                <ProvenanceErrorBoundary onClose={() => setSelectedCell(null)}>
                    <ProvenancePanel
                        workspaceId={workspaceId}
                        scenarioId={scenarioId}
                        nodeId={selectedCell.nodeId}
                        nodeLabel={selectedCell.label}
                        monthKey={selectedCell.monthKey}
                        monthLabel={selectedCell.monthLabel}
                        value={selectedCell.value}
                        isForecast={selectedCell.isForecast}
                        onClose={() => setSelectedCell(null)}
                        onOpenTransactions={handleOpenTransactions}
                        source="pnl"
                        accountCode={selectedCell.accountCode}
                    />
                </ProvenanceErrorBoundary>
            )}
        </div>
    );
}
